import { useEffect, useState } from 'react';
import { Alert, Box, Group, Loader, Text } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import api from '../../api/axiosConfig';
import PdfStudioEditor from './PdfStudioEditor.jsx';
import PdfStudioAuditPanel from './PdfStudioAuditPanel.jsx';
import './pdfFonts.js';
import './PdfStudioPortal.css';

/**
 * Portal kullanım şekli: <PdfStudio />
 * - currentUser, role, apiPrefix veya ayrı bir token parametresi GEREKMEZ.
 * - axiosConfig.js /token ile alınan JWT'yi localStorage['token'] üzerinden ekler.
 * - Yalnızca görünürlük için localStorage['user_role'] okunur.
 * - Gerçek kullanıcı ve yönetici yetkisini FastAPI dependency'leri kontrol eder.
 */
export default function PdfStudio() {
  const [auditOpened, setAuditOpened] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => localStorage.getItem('user_role')?.toLowerCase() === 'admin'
  );
  const [checking, setChecking] = useState(true);
  const [backendError, setBackendError] = useState('');
  const hasToken = Boolean(localStorage.getItem('token'));

  useEffect(() => {
    let active = true;
    const checkConnection = async () => {
      setChecking(true);
      try {
        const res = await api.get('/native-api/health');
        if (active) {
          setBackendError(res?.data?.status === 'ok' ? '' : 'PDF Studio motoru beklenmeyen yanıt verdi.');
        }
      } catch (error) {
        if (active) {
          setBackendError(
            error?.response?.status === 404
              ? 'PDF Studio router FastAPI üzerinde bulunamadı. Backend main.py içinde app.include_router(pdf_studio_router) bağlantısını kontrol edin.'
              : `PDF Studio backend bağlantısı başarısız: ${error?.message || 'Sunucuya erişilemiyor.'}`
          );
        }
      } finally {
        if (active) setChecking(false);
      }
    };
    const refreshRole = () => setIsAdmin(localStorage.getItem('user_role')?.toLowerCase() === 'admin');
    void checkConnection();
    window.addEventListener('storage', refreshRole);
    window.addEventListener('focus', refreshRole);
    return () => {
      active = false;
      window.removeEventListener('storage', refreshRole);
      window.removeEventListener('focus', refreshRole);
    };
  }, []);

  if (!hasToken) {
    return (
      <Alert color="red" title="Portal oturumu bulunamadı" icon={<IconAlertCircle size={18}/> }>
        PDF Studio'yu kullanabilmek için portala giriş yapmanız gerekiyor.
      </Alert>
    );
  }

  return (
    <Box className="pdf-studio-module">
      {(checking || backendError) && (
        <Box px="md" pt="md">
          {checking ? (
            <Group gap="xs"><Loader size="xs"/><Text size="xs" c="dimmed">PDF motoru bağlantısı kontrol ediliyor...</Text></Group>
          ) : (
            <Alert color="red" title="PDF motoruna erişilemiyor" icon={<IconAlertCircle size={18}/>}>
              {backendError}
              <Text size="xs" mt={6}>API hedefi: Portalın mevcut axiosConfig.js içindeki baseURL değeri</Text>
            </Alert>
          )}
        </Box>
      )}
      {!checking && !backendError && (
        <Box className="pdf-studio-health" aria-label="PDF motoru bağlantısı başarılı">
          <IconCircleCheck size={13} /> PDF motoru bağlı
        </Box>
      )}
      <PdfStudioEditor isAdmin={isAdmin} onOpenAudit={() => setAuditOpened(true)} />
      {isAdmin && (
        <PdfStudioAuditPanel opened={auditOpened} onClose={() => setAuditOpened(false)} />
      )}
    </Box>
  );
}
